/**
 * The parts every auth screen would otherwise get subtly wrong.
 *
 * Failure handling, focus movement and submission state are the same problem on
 * all seven screens, and they are the parts a person only notices when they are
 * broken: a form that submits twice, an error nobody announces, a heading that
 * changes while the screen reader is still on the old one.
 *
 * Nothing here parses an error message. Every branch switches on `ApiError.kind`,
 * which is the machine-readable contract the two clients guarantee.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, type ApiErrorKind } from '../api/client';
import { Alert } from '../design/Feedback';

/** A failure, reduced to what a screen has to render. */
export interface AuthFailure {
  kind: ApiErrorKind | 'unknown';
  /** One sentence, already in MusicMentor's voice. */
  message: string;
  /** Field name → message, for errors the server attributed to an input. */
  fields: Record<string, string>;
  /** True when pressing the same button again could plausibly work. */
  retryable: boolean;
  /** Present on a rate limit, when the server said how long to wait. */
  retryAfterSeconds?: number;
}

/**
 * What went wrong, in a form a screen can render.
 *
 * `offline` deliberately reassures rather than apologises: the learner's work is
 * genuinely still on the iPad, and saying so is the difference between a scary
 * error and a status note.
 */
export function describeFailure(error: unknown): AuthFailure {
  if (!(error instanceof ApiError)) {
    return {
      kind: 'unknown',
      message: 'Something went wrong. Try again.',
      fields: {},
      retryable: true,
    };
  }

  const fields: Record<string, string> = {};
  for (const field of error.fields) {
    // allauth names the signup password field `password`; the form does too.
    if (field.field && !fields[field.field]) fields[field.field] = field.message;
  }

  const message = (() => {
    switch (error.kind) {
      case 'offline':
        return 'You are offline. Your work is still saved on this iPad.';
      case 'timeout':
        return 'The server took too long to answer. Try again.';
      case 'rate-limited':
        return error.retryAfterSeconds
          ? `Too many attempts. Try again in ${describeWait(error.retryAfterSeconds)}.`
          : 'Too many attempts. Wait a few minutes and try again.';
      case 'server':
        return 'Something went wrong on our side. Your work is safe on this iPad.';
      case 'forbidden':
        return 'That request was refused. Reload the page and try again.';
      case 'validation':
        // The server's own wording is better than anything generic here.
        return error.message || 'Check the highlighted fields.';
      default:
        return error.message || 'Something went wrong. Try again.';
    }
  })();

  return {
    kind: error.kind,
    message,
    fields,
    retryable: error.retryable,
    retryAfterSeconds: error.retryAfterSeconds,
  };
}

function describeWait(seconds: number): string {
  if (seconds < 60) return `${Math.ceil(seconds)} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? 'a minute' : `${minutes} minutes`;
}

/**
 * Move focus to a screen's heading when it appears.
 *
 * Without this, navigating from Sign in to Create account leaves focus on a
 * button that no longer exists, and a screen reader announces nothing at all —
 * the single most common accessibility defect in a client-rendered auth flow.
 *
 * The heading takes `tabIndex={-1}` so it can receive focus programmatically
 * without joining the tab order.
 */
export function useHeadingFocus<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    // A frame later, once it exists, but without moving Safari's visual viewport.
    // The auth shell owns its scrolling; focus is for announcement only.
    const id = requestAnimationFrame(() => ref.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(id);
  }, []);
  return ref;
}

/**
 * Submission state for one form.
 *
 * `busy` both disables the button and is what the button reports as `aria-busy`,
 * so "cannot be pressed twice" and "is announced as busy" cannot drift apart.
 */
export function useSubmission() {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * Runs `work`, holding the form busy and turning any throw into a rendered
   * failure. Returns whether it succeeded, so callers can navigate on success
   * without a second try/catch.
   */
  async function submit(work: () => Promise<void>): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setFailure(null);
    try {
      await work();
      return true;
    } catch (error) {
      if (alive.current) setFailure(describeFailure(error));
      return false;
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return { busy, failure, setFailure, submit };
}

/**
 * The failure banner — and the decision about what announces a failure.
 *
 * A form field already renders its own error with `role="alert"`. If this banner
 * also announced the same sentence, a screen reader would interrupt itself with
 * two live regions saying identical things, and the person would hear the
 * message twice before finding out which field it belongs to.
 *
 * So exactly one of the two speaks. When every field error the server sent
 * belongs to a field this form actually renders, the inline errors are better —
 * they are attached to the control that is wrong — and focus moves to the first
 * of them instead. The banner is for everything a field cannot own: offline, a
 * rate limit, a timeout, a server fault, or an error naming a field that is not
 * on this screen.
 *
 * `handledFields` is what the form renders. Getting it wrong is safe in the only
 * direction that matters: an unlisted field falls back to the banner, so a
 * failure is never silent.
 */
export function FailureSummary({
  failure,
  handledFields = [],
}: {
  failure: AuthFailure | null;
  handledFields?: string[];
}) {
  const ref = useRef<HTMLDivElement>(null);

  const names = failure ? Object.keys(failure.fields) : [];
  const coveredByFields =
    names.length > 0 && names.every((name) => handledFields.includes(name));

  useEffect(() => {
    if (!failure) return;
    if (coveredByFields) {
      // Land on the problem itself. The field's own `role="alert"` announces it.
      const firstInvalid = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      firstInvalid?.focus();
    } else {
      ref.current?.focus();
    }
  }, [failure, coveredByFields]);

  if (!failure || coveredByFields) return null;

  // Offline is a condition, not a mistake, so it is toned as a warning while a
  // genuine failure is toned as danger.
  const tone = failure.kind === 'offline' ? 'warning' : 'danger';

  return (
    <div ref={ref} tabIndex={-1} className="mm-auth__summary">
      <Alert tone={tone}>{failure.message}</Alert>
    </div>
  );
}

/** A success note that replaces the form rather than sitting under it. */
export function SuccessPanel({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const ref = useHeadingFocus<HTMLHeadingElement>();
  return (
    <div className="mm-auth__success">
      <h2 className="mm-auth__success-title" ref={ref} tabIndex={-1}>
        {title}
      </h2>
      <div className="mm-auth__success-body">{children}</div>
      {actions ? <div className="mm-auth__actions">{actions}</div> : null}
    </div>
  );
}
